import { fileObject } from "@boark/shared/types/file-object";
import {
  faDownload,
  faFolderMinus,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";

import { useGetFiles } from "../queries/get-files";
import { formatDateTime } from "../utils/date";
import { formatSize } from "../utils/format-size";
import { deleteRequest } from "../utils/request";
import { Button } from "./button";
import { FileIcon } from "./file-icon";
import { FileUpload, FileUploadFolderOption } from "./file-upload";
import { Column, PrefixRow, Table } from "./table";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

type Category = { name: string; label: string };
type ObjectWithLabel = fileObject & { label: string; isCategory: boolean };

export function FileList({
  rootPath,
  categories,
  uploadFolderOptions,
  uploadFolderLabel,
}: {
  rootPath: string;
  categories?: Category[];
  uploadFolderOptions?: FileUploadFolderOption[];
  uploadFolderLabel?: string;
}) {
  const [path, setPath] = useState(rootPath);
  const { data: objects, invalidate } = useGetFiles(path);

  const [sort, setSort] = useState<{
    field: string;
    direction: "asc" | "desc";
  }>({ field: "name", direction: "asc" });

  const isRoot = useMemo(
    () => path.length === rootPath.length,
    [path, rootPath],
  );

  const objectsWithCategories = useMemo<ObjectWithLabel[]>(() => {
    if (!categories || categories.length === 0 || !isRoot)
      return (
        objects?.map((object) => ({
          ...object,
          label: object.name,
          isCategory: false,
        })) ?? []
      );

    return [
      ...(objects ?? []).map((object: fileObject) => {
        const existingCategory = categories.find(
          (category) => object.name === category.name,
        );
        return {
          ...object,
          label: existingCategory ? existingCategory.label : object.name,
          name: object.name,
          isCategory: !!existingCategory,
        };
      }),
      ...categories
        .filter(
          (category) =>
            !objects?.some((object) => object.name === category.name),
        )
        .map((category) => ({
          name: category.name,
          label: category.label,
          type: "folder",
          isCategory: true,
        })),
    ] as ObjectWithLabel[];
  }, [objects, categories, isRoot]);

  return (
    <div className="">
      <Button onClick={() => void invalidate()}>
        <FontAwesomeIcon icon={faRefresh} />
      </Button>
      <Table
        actions={(row: ObjectWithLabel) => [
          ...(!row.isCategory
            ? [
                {
                  label: "Delete",
                  onClick: (row: fileObject) => {
                    (async () => {
                      await deleteRequest(
                        `${API_BASE_URL}/file?path=${path}/${row.name}`,
                      );
                      await invalidate();
                    })();
                  },
                },
              ]
            : []),
        ]}
        data={objectsWithCategories}
        sortBy={sort.field}
        sortByDirection={sort.direction}
        onSort={(field, direction) => setSort({ field, direction })}
      >
        {!isRoot && (
          <PrefixRow>
            <td className="!text-center">
              <Button text={true} onClick={() => setPath("")}>
                <FontAwesomeIcon icon={faFolderMinus} />
              </Button>
            </td>
            <td colSpan={5}>
              <Button
                text={true}
                onClick={() => setPath(path.split("/").slice(0, -1).join("/"))}
              >
                ...
              </Button>
            </td>
          </PrefixRow>
        )}
        <Column
          body={(row: ObjectWithLabel) => <FileIcon object={row} />}
          bodyClassName="!text-center"
          field="type"
        />
        <Column
          body={(row: ObjectWithLabel) =>
            row.type === "folder" ? (
              <Button
                text={true}
                onClick={() => setPath(`${path}/${row.name}`)}
              >
                {row.label}
              </Button>
            ) : (
              row.label
            )
          }
          field="name"
          sortCallback={(
            a: ObjectWithLabel,
            b: ObjectWithLabel,
            _field: string,
            direction: "asc" | "desc",
          ) => {
            // 1. folders before files
            if (a.type !== b.type) {
              return a.type === "folder" ? -1 : 1;
            }

            // 2. alphabetical by name
            return (
              a.name.localeCompare(b.name) * (direction === "asc" ? 1 : -1)
            );
          }}
        />
        <Column
          body={(row: ObjectWithLabel) =>
            "size" in row ? formatSize(row.size) : "-"
          }
          field="size"
        />
        <Column
          body={(row: ObjectWithLabel) =>
            "lastModified" in row
              ? formatDateTime(row.lastModified, false)
              : "-"
          }
          field="lastModified"
        />
        <Column
          body={(row: fileObject) =>
            row.type === "file" ? (
              <>
                <Button
                  href={`${API_BASE_URL}/download?path=${encodeURIComponent(`${path}/${row.name}`)}`}
                  size="sm"
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </Button>
              </>
            ) : (
              "-"
            )
          }
          bodyClassName="!text-center"
          sortable={false}
        />
      </Table>
      <FileUpload
        folderLabel={uploadFolderLabel}
        folderOptions={isRoot ? uploadFolderOptions : undefined}
        path={path}
        onUpload={() => void invalidate()}
      />
    </div>
  );
}
